import { useEffect, useState, useCallback } from "react";
import { Wifi, WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";
import api from "@/utils/api";
import { flushQueue, queueLength } from "@/utils/offlineQueue";

/* Floating, low-key indicator bar. Shows up only when:
 *   - The browser reports `offline`, OR
 *   - There are queued submissions waiting to be replayed.
 * Replays the queue automatically when `online` event fires.
 */
const OfflineSyncBanner = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const [justSent, setJustSent] = useState(0);

  const refreshCount = useCallback(async () => {
    try { setPending(await queueLength()); } catch { /* IDB unavailable */ }
  }, []);

  const doFlush = useCallback(async () => {
    setFlushing(true);
    try {
      const { sent } = await flushQueue(api);
      if (sent > 0) {
        setJustSent(sent);
        setTimeout(() => setJustSent(0), 4000);
      }
    } catch { /* ignored */ }
    setFlushing(false);
    refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    const onOnline = () => { setOnline(true); doFlush(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // periodic sweep — handles rare cases where `online` event was missed
    const t = setInterval(() => {
      refreshCount();
      if (navigator.onLine) doFlush();
    }, 30 * 1000);
    // expose for forms to nudge after enqueueing
    window.__amplOfflineRefresh = refreshCount;
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(t);
      delete window.__amplOfflineRefresh;
    };
  }, [refreshCount, doFlush]);

  if (online && pending === 0 && justSent === 0) return null;

  let bg = "bg-amber-500";
  let icon = <WifiOff size={14} />;
  let text = "Offline — submissions will sync when back online";
  if (justSent > 0 && online) {
    bg = "bg-[#15803D]";
    icon = <CheckCircle2 size={14} />;
    text = `Synced ${justSent} pending submission${justSent > 1 ? "s" : ""}`;
  } else if (online && pending > 0) {
    bg = "bg-[#1A3560]";
    icon = flushing ? <RefreshCw size={14} className="animate-spin" /> : <Wifi size={14} />;
    text = `${pending} pending submission${pending > 1 ? "s" : ""} — ${flushing ? "syncing…" : "tap to retry"}`;
  } else if (!online) {
    text = pending > 0
      ? `Offline — ${pending} submission${pending > 1 ? "s" : ""} queued, will sync when back online`
      : "Offline — submissions will sync when back online";
  }

  return (
    <button
      data-testid="offline-banner"
      onClick={online ? doFlush : undefined}
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 ${bg} text-white px-4 py-2 rounded-full shadow-lg text-xs font-bold flex items-center gap-2 active:scale-95 transition-all`}
    >
      {icon} {text}
    </button>
  );
};

export default OfflineSyncBanner;
